import { INVENTORY_FIELD_LABELS } from "../config/inventoryFields";

/**
 * Gets the display label for an input column name by mapping it to inventory column name
 * Maps: input_column_name → inventory_column_name → display label
 *
 * @param {string} inputColumnName - The input column name (e.g., "NIT", "Bundle Type")
 * @param {Object} fieldMappingLookup - Mapping from input_column_name to inventory_column_name
 *                                     Example: { "NIT": "circuit_id", "Bundle Type": "service_type" }
 * @param {Object} inventoryFieldLabels - Optional override for INVENTORY_FIELD_LABELS (defaults to imported constant)
 * @returns {string} Display label for the column (e.g., "Circuit ID", "Service Type")
 */
export const getInventoryColumnLabel = (
  inputColumnName,
  fieldMappingLookup = {},
  inventoryFieldLabels = INVENTORY_FIELD_LABELS
) => {
  if (!inputColumnName || typeof inputColumnName !== "string") {
    return "";
  }

  // If fieldMappingLookup is empty or doesn't have the input column, fall back to formatted input name
  if (
    !fieldMappingLookup ||
    Object.keys(fieldMappingLookup).length === 0 ||
    !fieldMappingLookup[inputColumnName]
  ) {
    // Fallback: format the input column name nicely
    return formatInputColumnName(inputColumnName);
  }

  // Get the inventory column name from mapping
  const inventoryColumnName = fieldMappingLookup[inputColumnName];

  // If inventory column name is not found or invalid, fall back to formatted input name
  if (!inventoryColumnName || typeof inventoryColumnName !== "string") {
    return formatInputColumnName(inputColumnName);
  }

  // Get display label from INVENTORY_FIELD_LABELS
  const displayLabel = inventoryFieldLabels[inventoryColumnName];

  // If display label exists, return it
  if (displayLabel) {
    return displayLabel;
  }

  // Final fallback: format the inventory column name
  return formatInputColumnName(inventoryColumnName);
};

/**
 * Formats an input column name for display by adding spaces before capital letters
 * and capitalizing each word. Also handles underscores by replacing them with spaces.
 * @param {string} columnName - The column name to format
 * @returns {string} Formatted column label
 */
const formatInputColumnName = (columnName) => {
  if (!columnName || typeof columnName !== "string") {
    return "";
  }

  return columnName
    .replace(/_/g, " ") // Replace underscores with spaces
    .replace(/([A-Z])/g, " $1") // Add space before capital letters
    .replace(/\s+/g, " ") // Replace multiple spaces with single space
    .trim()
    .split(" ") // Split into words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
    .join(" "); // Join back with spaces
};
